/**
 * TrainingInsights schema (camelCase) — matches what
 * services/background/post_training_analysis_service.py writes into
 * `trainingsessions.<id>.trainingInsights` after a session completes.
 *
 * Mirrors the post-call adherence shape where it makes sense, but DROPS the
 * fields that don't fit a training simulation:
 *   - speakerStats (AI customer talk-ratio is meaningless — it's a system output)
 *   - salesIntelligence.phases (no organic call phases in training)
 *   - closingSignal, dealRiskSignals, rapportMoments (no real deal to close)
 *
 * REPLACES the phase view with a training-appropriate progression view:
 *   - kbItemProgression — how the rep handled each KB item the AI surfaced
 *   - playbookPhaseProgression — coverage per playbook phase (if playbook enabled)
 */

// ---------------------------------------------------------------------------
// Per-section item interfaces
// ---------------------------------------------------------------------------

export interface ObjFaqItem {
  question: string;
  itemType: 'objection' | 'faq';
  /** ISO timestamp of when this item was handled — used to jump to the
   *  matching spot in the session's conversation timeline. */
  timestampIso?: string;
  handlingScore: number;
  adherenceLabel: 'strong' | 'partial' | 'missed' | 'harmful';
  adherenceSummary: string;
  whatWorked: string;
  whatMissed: string;
  whatRepShouldHaveSaid: string;
  pushbackHandling: string;
  keywordsUsed: string[];
  keywordsMissed: string[];
}

export interface LandmineItem {
  trigger: string;
  timestampIso?: string;
  wasTriggered: boolean;
  handlingScore: number;
  handlingSummary: string;
  whatRepSaid: string;
  recommendedResponse: string;
}

export interface CompetitorItem {
  competitorName: string;
  timestampIso?: string;
  handlingScore: number;
  handlingSummary: string;
  advantagesUsed: string[];
  advantagesMissed: string[];
  missedOpportunity: string;
  recommendedApproach: string;
}

export interface PlaybookCoverage {
  wasUsed: boolean;
  overallScore: number | null;  // null = N/A (no playbook on this track)
  overallSummary: string;
  introCoverage: {
    score: number;
    coveredPoints: string[];
    missedPoints: string[];
    coachingNote: string;
  };
  questionCoverage: {
    score: number;
    tagsCovered: string[];
    tagsMissed: string[];
    questionsCovered: string[];
    questionsMissed: string[];
    coachingNote: string;
  };
  dealProgressionCoverage: {
    score: number;
    coveredPoints: string[];
    missedPoints: string[];
    coachingNote: string;
  };
}

export interface KbItemProgression {
  trackItemId: string;
  itemType: 'objection' | 'faq' | 'landmine' | 'competitor' | string;
  question: string;
  turnSurfaced: number;
  turnAddressed: number | null;
  handlingScore: number;
  pushbackRoundsUsed: number;
  oneLineSummary: string;
  /** Only set when the session was abandoned AND the rep never engaged
   *  with this item. Treated visually as "grey / not attempted" rather
   *  than a low-score "missed" — fair scoring for incomplete sessions. */
  status?: 'not_attempted';
}

export interface PlaybookPhaseProgression {
  phase: 'intro' | 'qualifying' | 'dealProgression';
  wasDelivered: boolean;
  coverageScore: number;
  missedPoints: string[];
  oneLineSummary: string;
}

export interface BuyingSignalMissed {
  signal: string;
  repResponse: string;
  suggestion: string;
}

export interface SalesIntelligence {
  buyingSignalsMissed: BuyingSignalMissed[];
  coachingForThisSession: string[];
  coachingForGeneralImprovement: string[];
  kbItemProgression: KbItemProgression[];
  playbookPhaseProgression: PlaybookPhaseProgression[];
}

export interface ToneScore {
  /** 0-100 — how strongly this quality was present in the rep's speech. */
  score: number;
  /** 0-100 — how confident the AI is in this particular score. */
  confidenceLevel: number;
  explanation: string;
}

export interface ToneAnalysis {
  positive: {
    confidence: ToneScore;
    optimism: ToneScore;
  };
  negative: {
    defensiveness: ToneScore;
    confrontation: ToneScore;
  };
  overallCoaching: string;
}

export interface OverallCoaching {
  behavioralScore: number;
  strengths: string[];
  improvements: string[];
  scoreDimensions: Record<string, number>;
  recommendedNextTrack: string;
  managerSummary: string;
}

// ---------------------------------------------------------------------------
// Top-level trainingInsights document
// ---------------------------------------------------------------------------

export type DimensionKey =
  | 'objection_faq'
  | 'landmines'
  | 'competitors'
  | 'playbook'
  | 'sales_intelligence'
  | 'tone_analysis'
  | 'summary_bullets'
  | 'overall_coaching';

export interface TrainingInsights {
  status: 'completed' | 'pending' | 'failed';
  /** True when the underlying training session was abandoned mid-conversation.
   *  The analyzer adapts (no penalty for items not attempted) and the UI
   *  renders a "Session Abandoned" badge. */
  wasAbandoned?: boolean;
  analyzedAt?: string;
  error?: string;

  objectionFaqHandling?: {
    overallScore: number | null;  // null = N/A (track has no items of this type)
    overallSummary: string;
    items: ObjFaqItem[];
  };
  landmineHandling?: {
    overallScore: number | null;  // null = N/A (track has no items of this type)
    overallSummary: string;
    items: LandmineItem[];
  };
  competitorHandling?: {
    overallScore: number | null;  // null = N/A (track has no items of this type)
    overallSummary: string;
    items: CompetitorItem[];
  };
  playbookCoverage?: PlaybookCoverage;
  salesIntelligence?: SalesIntelligence;
  toneAnalysis?: ToneAnalysis;
  trainingSummaryBullets?: string[];
  overallCoaching?: OverallCoaching;

  /** Names of dimensions whose analysis has completed. Used to render
   *  partial progress ("5 of 8 done") during the async generation window. */
  dimensionsComplete?: DimensionKey[];
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

export function scoreColor(
  score: number | null | undefined,
): 'success' | 'warning' | 'error' | 'default' {
  if (score == null) return 'default';   // N/A — neutral grey
  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  return 'error';
}

/** Render a dimension score for display: "N/A" when the score is
 *  null/undefined (the track had no items of that type), else "NN%". */
export function formatScore(score: number | null | undefined): string {
  return score == null ? 'N/A' : `${score}%`;
}

/** scoreColor variant safe for MUI <LinearProgress color>, which does
 *  NOT accept 'default'. Maps the N/A case to a neutral 'info'. */
export function barColor(
  score: number | null | undefined,
): 'success' | 'warning' | 'error' | 'info' {
  const c = scoreColor(score);
  return c === 'default' ? 'info' : c;
}
